/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  useState,
  useMemo,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { Channel, LocalMessage, StreamChat } from 'stream-chat';
import { useCustomCreateChatClient } from 'hooks';
import { ContextualChatController } from 'api';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'components/store/store';
import { setChatButtonSettings } from 'components/store/chatButtonRedux';
import { isContextualChatEnabled } from 'utils';
import AuthContext from './AuthContext';

export enum LeftSidePanelPageType {
  NewChat = 'createNewChat',
  ChannelList = 'channelList',
}
export enum RightSidePanelPageType {
  ChatDetails = 'chatDetailsPanel',
  ContactDetails = 'contactDetailsPanel',
  MediaLinkDocDetails = 'mediaLinkDocDetailsPanel',
  SearchInChat = 'searchInChatPanel',
  ChannelThread = 'ChannelThread',
}

export enum ChannelType {
  'messaging' = 'messaging',
  'listing' = 'listing',
}

export type ChatButtonSettingsType = {
  enableCreateListingChat: boolean;
  repIds: string[];
  listingId: string;
  listing?: todo;
  userIdToCreateChannelId?: string;
};

type ChatButtonDisplayTextType =
  | 'Messages'
  | 'Message Agents'
  | 'Message Agent';

export type ContextualChatContextType = {
  client: StreamChat | null;
  channel: Channel | null;
  setChannel: Dispatch<SetStateAction<Channel | null>>;
  chatModalVisible: boolean;
  setChatModalVisible: (visible: boolean) => void;
  leftSidePanelPage: LeftSidePanelPageType;
  setLeftSidePanelPage: (sidePanel: LeftSidePanelPageType) => void;
  rightSidePanelPage: RightSidePanelPageType;
  setRightSidePanelPage: (sidePanel: RightSidePanelPageType) => void;
  viewDetailsPanel: boolean;
  setViewDetailsPanel: (visible: boolean) => void;
  selectedContactId: string;
  setSelectedContactId: (contactId: string) => void;
  buttonSettings: ChatButtonSettingsType;
  setButtonSettings: Dispatch<SetStateAction<ChatButtonSettingsType>>;
  chatButtonDisplayText: ChatButtonDisplayTextType;
  creatingChatLoading: boolean;
  setCreatingChatLoading: (isLoading: boolean) => void;
  unreadCount: number | false; // if false, we show '!' on badge indicating connection error
  hasConnectionError?: boolean;
  retryConnection: () => void;
  isConnecting?: boolean;
  channelText: string;
  setChannelText: Dispatch<SetStateAction<string>>;
  channelAttachments: File[];
  setChannelAttachments: Dispatch<SetStateAction<File[]>>;
  expandedChannel: boolean;
  setExpandedChannel: Dispatch<SetStateAction<boolean>>;
  quotedMessage?: LocalMessage;
  setQuotedMessage: (localMessage: LocalMessage) => void;
  channelIdToTop: string | null;
  setChannelIdToTop: Dispatch<SetStateAction<string | null>>;
  highlightedId: string;
  setHighlightedId: Dispatch<SetStateAction<string>>;
};

const ContextualChatContext = React.createContext<ContextualChatContextType>({
  client: null,
  channel: null,
  setChannel: () => {},
  chatModalVisible: false,
  setChatModalVisible: () => {},
  leftSidePanelPage: LeftSidePanelPageType.ChannelList,
  setLeftSidePanelPage: () => {},
  rightSidePanelPage: RightSidePanelPageType.ChatDetails,
  setRightSidePanelPage: () => {},
  viewDetailsPanel: false,
  setViewDetailsPanel: () => {},
  selectedContactId: '',
  setSelectedContactId: () => {},
  buttonSettings: {
    enableCreateListingChat: false,
    repIds: [],
    listingId: '',
    userIdToCreateChannelId: '',
  },
  setButtonSettings: () => {},
  chatButtonDisplayText: 'Messages',
  creatingChatLoading: false,
  setCreatingChatLoading: () => {},
  unreadCount: 0,
  hasConnectionError: false,
  retryConnection: () => {},
  isConnecting: false,
  channelText: '',
  setChannelText: () => {},
  channelAttachments: [],
  setChannelAttachments: () => {},
  setExpandedChannel: () => {},
  expandedChannel: true,
  setQuotedMessage: () => {},
  channelIdToTop: null,
  setChannelIdToTop: () => {},
  highlightedId: '',
  setHighlightedId: () => {},
});

export const ContextualChatContextProvider: React.FC = ({ children }) => {
  const reduxDispatch = useDispatch();
  const buttonSettings = useSelector((state: RootState) => state.chatButton);

  const setButtonSettings: Dispatch<SetStateAction<ChatButtonSettingsType>> =
    useCallback(
      updater => {
        if (typeof updater === 'function') {
          const current = (store.getState() as RootState).chatButton;
          const next = (
            updater as (prev: ChatButtonSettingsType) => ChatButtonSettingsType
          )(current);
          reduxDispatch(setChatButtonSettings(next));
        } else {
          reduxDispatch(setChatButtonSettings(updater));
        }
      },
      [reduxDispatch],
    );

  const [expandedChannel, setExpandedChannel] = useState(true);
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [leftSidePanelPage, setLeftSidePanelPage] =
    useState<LeftSidePanelPageType>(LeftSidePanelPageType.ChannelList);
  const [rightSidePanelPage, setRightSidePanelPage] =
    useState<RightSidePanelPageType>(RightSidePanelPageType.ChatDetails);
  const [viewDetailsPanel, setViewDetailsPanel] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [channelText, setChannelText] = useState('');
  const [channelAttachments, setChannelAttachments] = useState<File[]>([]);
  const [creatingChatLoading, setCreatingChatLoading] = useState(false);
  const [channel, setChannel] = useState<Channel | null>(null);
  const [quotedMessage, setQuotedMessage] = useState<LocalMessage>();
  const [unreadCount, setUnreadCount] = useState(0);
  /**
   * State to trigger moving a channel to the top of the list outside of the channel list component
   * e.g. used in startChat after creating or opening a channel
   */
  const [channelIdToTop, setChannelIdToTop] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string>('');

  const {
    auth: { streamChatId, org: { mlsTypes } = {} },
    hasFeature,
  } = useContext(AuthContext);

  const contextualChatEnabledForUser = useMemo(
    () => isContextualChatEnabled(mlsTypes),
    [mlsTypes],
  );

  const chatClientConfig = useMemo(
    () => ({
      apiKey: process.env.STREAM_CHAT_API_KEY,
      tokenOrProvider: ContextualChatController.getAccessToken,
      userData: { id: streamChatId },
      enabled: hasFeature('CHAT') && contextualChatEnabledForUser,
    }),
    [streamChatId, hasFeature, contextualChatEnabledForUser],
  );

  const { client, retryConnection, failedConnection, isConnecting } =
    useCustomCreateChatClient(chatClientConfig);

  useEffect(() => {
    if (!client || !contextualChatEnabledForUser) return;

    // @ts-expect-error ts issues from stream chat side
    setUnreadCount(client.user?.total_unread_count || 0);

    const updateUnreadCount = (event: any) => {
      if (typeof event.total_unread_count === 'number') {
        setUnreadCount(event.total_unread_count);
      }
    };

    // Subscribe to events that affect unread count
    client.on('notification.mark_read', updateUnreadCount);
    client.on('message.new', updateUnreadCount);
    client.on('notification.message_new', updateUnreadCount);
    client.on('notification.channel_deleted', updateUnreadCount);

    return () => {
      client.off('notification.mark_read', updateUnreadCount);
      client.off('message.new', updateUnreadCount);
      client.off('notification.message_new', updateUnreadCount);
      client.off('notification.channel_deleted', updateUnreadCount);
    };
  }, [client]);

  const chatButtonDisplayText = useMemo(() => {
    const { repIds, enableCreateListingChat, listingId } = buttonSettings;
    if (repIds.length > 1 && enableCreateListingChat && listingId)
      return 'Message Agents';
    if (
      repIds.length === 1 &&
      repIds[0].length &&
      enableCreateListingChat &&
      listingId
    )
      return 'Message Agent';
    return 'Messages';
  }, [buttonSettings]);

  const resetChannelInput = () => {
    setChannelAttachments([]);
    setChannelText('');
    setQuotedMessage(undefined);
  };

  useEffect(() => {
    resetChannelInput();
  }, [channel]);

  return (
    <ContextualChatContext.Provider
      value={{
        highlightedId,
        setHighlightedId,
        client,
        channel,
        setChannel,
        chatModalVisible,
        setChatModalVisible,
        leftSidePanelPage,
        setLeftSidePanelPage,
        rightSidePanelPage,
        setRightSidePanelPage,
        viewDetailsPanel,
        setViewDetailsPanel,
        selectedContactId,
        setSelectedContactId,
        buttonSettings,
        setButtonSettings,
        chatButtonDisplayText,
        creatingChatLoading,
        setCreatingChatLoading,
        unreadCount,
        hasConnectionError: failedConnection,
        retryConnection,
        isConnecting,
        channelText,
        channelAttachments,
        setChannelAttachments,
        setChannelText,
        expandedChannel,
        setExpandedChannel,
        quotedMessage,
        setQuotedMessage,
        channelIdToTop,
        setChannelIdToTop,
      }}
    >
      {children}
    </ContextualChatContext.Provider>
  );
};

export default ContextualChatContext;
